import { useState, useEffect } from "react"
import {
  Avatar,
  Box,
  Button,
  Container,
  CssBaseline,
  TextField,
  Typography,
  CircularProgress,
  Backdrop,
  Alert,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material"
import { LockClockOutlined, Visibility, VisibilityOff, DoneOutline } from "@mui/icons-material"
import { api } from "../../../services/Main/Api"
import { getToken, removeTokens } from "../../../services/Main/AuthToken"
import { removeLocalUser, removeCondoId, removeCondoName, removeFilterDate } from "../../../services/Main/AuthStorage"
import { removeTokensUnitUser } from "../../../services/UnitUser/UnitUserToken"
import { useNavigate } from "react-router-dom"
import { Helmet } from "react-helmet"
import { useSnackbar } from "notistack"
import logo from "../../../assets/images/blocob3-logo.png"

const SetFirstPassword = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const [errorNewPassword, setErrorNewPassword] = useState({ error: false, message: "" })
  const [errorConfirmPassword, setErrorConfirmPassword] = useState({ error: false, message: "" })

  useEffect(() => {
    if (!getToken()) {
      navigate("/login", { replace: true })
    }
  }, [])

  const clearErrors = () => {
    setErrorNewPassword({ error: false, message: "" })
    setErrorConfirmPassword({ error: false, message: "" })
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    clearErrors()

    if (!newPassword.trim()) {
      setErrorNewPassword({ error: true, message: "Esse campo não pode ser vazio." })
      return
    }
    if (!confirmPassword.trim()) {
      setErrorConfirmPassword({ error: true, message: "Esse campo não pode ser vazio." })
      return
    }
    if (newPassword !== confirmPassword) {
      setErrorNewPassword({ error: true, message: "As senhas devem ser iguais." })
      setErrorConfirmPassword({ error: true, message: "As senhas devem ser iguais." })
      return
    }

    setIsLoading(true)

    api.post("user/set-first-password/", {
      new_password: newPassword,
      confirm_new_password: confirmPassword,
    })
      .then(() => {
        setSuccess(true)
        removeTokens()
        removeTokensUnitUser()
        removeLocalUser()
        removeCondoId()
        removeCondoName()
        removeFilterDate()
        enqueueSnackbar("Senha definida com sucesso! Faça login com sua nova senha.", { variant: "success" })
        setTimeout(() => navigate("/login"), 3000)
      })
      .catch((error) => {
        const data = error?.response?.data
        if (data?.new_password) {
          const msg = Array.isArray(data.new_password) ? data.new_password[0] : data.new_password
          setErrorNewPassword({ error: true, message: msg })
        }
        if (data?.confirm_new_password) {
          const msg = Array.isArray(data.confirm_new_password) ? data.confirm_new_password[0] : data.confirm_new_password
          setErrorConfirmPassword({ error: true, message: msg })
        }
        if (data?.detail) {
          enqueueSnackbar(data.detail, { variant: "error" })
        }
      })
      .finally(() => setIsLoading(false))
  }

  return (
    <>
      <Helmet>
        <title>Definir Senha - Bloco B3</title>
      </Helmet>
      <CssBaseline />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {success ? (
        <Container component="main" maxWidth="xs">
          <Box
            sx={{
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: "secondary.main", height: 54, width: 54 }}>
              <DoneOutline fontSize="large" />
            </Avatar>
            <Typography component="h4" variant="h4" sx={{ mt: 2, mb: 2 }}>
              {"Sucesso!"}
            </Typography>
            <Typography sx={{ mb: 2 }}>{"Redirecionando para o login..."}</Typography>
            <CircularProgress sx={{ color: "primary.main", mb: 2 }} />
            <Alert sx={{ mt: 2, mb: 4, backgroundColor: "transparent" }} variant="outlined">
              {"Senha definida com sucesso. Você será redirecionado para a tela de login."}
            </Alert>
            <Link href="/login" variant="body2">
              {"Ir para o login"}
            </Link>
          </Box>
        </Container>
      ) : (
        <Container component="main" maxWidth="xs">
          <Box
            sx={{
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar variant="square" sx={{ m: 1, height: 64, width: 64 }} src={logo}>
              <LockClockOutlined fontSize="large" />
            </Avatar>
            <Typography component="h1" variant="h5" sx={{ mt: 1 }}>
              {"Definir sua senha"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center" }}>
              {"Crie uma senha para acessar sua conta. A senha deve ter pelo menos 8 caracteres."}
            </Typography>

            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3, width: "100%" }}>
              <TextField
                label="Nova senha"
                name="new_password"
                margin="normal"
                required
                fullWidth
                size="small"
                type={showNewPassword ? "text" : "password"}
                error={errorNewPassword.error}
                helperText={errorNewPassword.message}
                onChange={({ target: { value } }) => setNewPassword(value)}
                value={newPassword}
                autoFocus
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showNewPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirmar senha"
                name="confirm_new_password"
                margin="normal"
                required
                fullWidth
                size="small"
                type={showConfirmPassword ? "text" : "password"}
                error={errorConfirmPassword.error}
                helperText={errorConfirmPassword.message}
                onChange={({ target: { value } }) => setConfirmPassword(value)}
                value={confirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress color="primary" size={28} /> : "Confirmar"}
              </Button>
            </Box>
          </Box>
        </Container>
      )}
    </>
  )
}

export default SetFirstPassword
